/*----------------------------------
- NEWSLETTER PAGE: CAPTURE OUTCOME
----------------------------------*/

/**
 * Turns the subscribe hook's status into what both captures of `/newsletter` draw: the marketing layer's state name
 * (the hero pill and the closing `NewsletterBand` share the band's names) and the line from the page catalog.
 *
 * @docs docs/features/newsletter
 * @rule Read the hook's status, never re-derive it: the request, the timing signal and the outcome belong to
 *       `useNewsletterSubscribe`, which this page does not change.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import type { NewsletterBandStatus } from '@uniquedomains/components/marketing/newsletter-band/NewsletterBand';

import { NEWSLETTER_PAGE_CONTENT } from '@/client/catalogs/landing/newsletter/pageContent';
import type { NewsletterFormStatus } from '@/client/pages/landing/components/newsletter/useNewsletterSubscribe';

/*----------------------------------
- TYPES
----------------------------------*/

export type NewsletterCaptureOutcome = {
    status: NewsletterBandStatus;
    /** The line under the capture, absent while idle or sending. */
    message?: string;
    /** A refusal: the field points at the line, which is an alert. */
    refused: boolean;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const STATUS_BY_HOOK_STATUS: Record<NewsletterFormStatus, NewsletterBandStatus> = {
    idle: 'idle',
    submitting: 'sending',
    subscribed: 'done',
    invalid: 'invalid',
    too_fast: 'too-fast',
    rate_limited: 'limited',
    error: 'error',
};

/*----------------------------------
- HELPERS
----------------------------------*/

export const toNewsletterCaptureOutcome = (status: NewsletterFormStatus): NewsletterCaptureOutcome => {
    const bandStatus = STATUS_BY_HOOK_STATUS[status];
    if (status === 'idle' || status === 'submitting') return { status: bandStatus, refused: false };
    return { status: bandStatus, message: NEWSLETTER_PAGE_CONTENT.form.outcomes[status], refused: status !== 'subscribed' };
};
